import React, { Suspense } from "react";
import { ResourceForm } from "src/components/Form/ResourceForm.tsx";
import { useParams } from "react-router-dom";
import { useModuleRoutePaths } from "src/system/useModuleRoutePaths.ts";
import type { ValidSaveFormResponseData } from "src/components/Form/hooks/useResourceForm/useResourceForm.tsx";
import { replacePathParams } from "@exonn/utils";

import { useModuleTabs } from "src/framework/layout/ModuleTabsProvider.tsx";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import { AccessDenied } from "src/components/AccessDenied/AccessDenied.tsx";
import { useModuleCreateDisabled } from "src/system/useModuleCreateDisabled.ts";
import { useModuleEditDisabled } from "src/system/useModuleEditDisabled.ts";
import type { FormAction } from "src/components/Form/Form.types.ts";
import { getModuleFirstTabPath } from "src/framework/openModule.ts";
import { useUser } from "src/auth/auth.ts";

export type ResourceRouteProps = {
  resource?: string;
  type: FormAction;
  tab?: string;
};

export const ResourceRoute = (props: ResourceRouteProps) => {
  const { type, resource: resourceProp, tab } = props;
  const params = useParams() as {
    id: string;
    resource?: string;
  };

  const resource = resourceProp || params["resource"];

  if (!resource) {
    throw new Error(
      "resource should be passed through props or be inferred from route params",
    );
  }

  const paths = useModuleRoutePaths({
    resource,
  });
  const { closeCreateTab, closeEditTab } = useModuleTabs();
  const { data: user } = useUser();

  const { router } = useDataRouterContext();

  const disableCreate = useModuleCreateDisabled(resource);
  const disableEdit = useModuleEditDisabled(resource);

  const handleSaveSuccess = (data: ValidSaveFormResponseData) => {
    if (type === "create") {
      // unfortunately there is such inconsistency in backend responses
      // sometimes backend returns just the id instead of the entire resource
      const id = data?.data?.id ?? data.id;
      router
        .navigate(
          replacePathParams(paths.edit.root, {
            id: id,
          }),
        )
        .then(() => {
          closeCreateTab();
        });
    }
  };

  const handleDeleteSuccess = () => {
    router
      .navigate(
        getModuleFirstTabPath({
          resource,
          user: user!,
        }),
      )
      .then(() => {
        closeEditTab(params);
      });
  };

  if (type === "create" && disableCreate) {
    return <AccessDenied />;
  }

  if (type === "edit" && disableEdit) {
    return <AccessDenied />;
  }

  // todo: loading overlay
  return (
    <Suspense
      // fallback={
      //   <LoadingOverlay.Root isLoading>
      //     <LoadingOverlay.Spinner />
      //     <LoadingOverlay.Title>Please wait!</LoadingOverlay.Title>
      //   </LoadingOverlay.Root>
      // }
      fallback={null}
    >
      <ResourceForm
        key={params.id + tab}
        tab={tab}
        id={params.id}
        action={type}
        resource={resource}
        onSaveSuccess={handleSaveSuccess}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </Suspense>
  );
};
